import { useEffect, useState, useRef } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { api, resolveImage } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Upload, X } from "lucide-react";

const Field = ({ label, name, value, onChange, type = "text", placeholder, testid, multiline, full }) => (
  <label className={`block ${full ? "md:col-span-2" : ""}`}>
    <span className="block text-[10px] tracking-[0.28em] uppercase text-black/50 mb-3">{label}</span>
    {multiline ? (
      <textarea
        data-testid={testid}
        name={name}
        value={value || ""}
        onChange={onChange}
        rows={5}
        placeholder={placeholder}
        className="w-full bg-transparent border-0 border-b border-black/20 rounded-none px-0 py-3 text-base focus:outline-none focus:border-black transition-colors resize-none"
      />
    ) : (
      <input
        data-testid={testid}
        type={type}
        name={name}
        value={value || ""}
        onChange={onChange}
        placeholder={placeholder}
        className="w-full bg-transparent border-0 border-b border-black/20 rounded-none px-0 py-3 text-base focus:outline-none focus:border-black transition-colors"
      />
    )}
  </label>
);

const TestimonialForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;
  const fileRef = useRef(null);

  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({
    client_name: "",
    photo_url: "",
    quote: "",
    city: "",
    context: "",
    transaction_type: "Buyer",
    year: String(new Date().getFullYear()),
    position: 0,
    featured: true,
  });

  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      try {
        const { data } = await api.get("/admin/testimonials");
        const found = data.find((t) => t.id === id);
        if (!found) {
          toast.error("Testimonial not found");
          navigate("/admin");
          return;
        }
        setForm({ ...form, ...found });
      } catch {
        toast.error("Failed to load testimonial");
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    })();
  }, [id]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({
      ...f,
      [name]: type === "checkbox" ? checked : (name === "position" ? Number(value) || 0 : value),
    }));
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((f) => ({ ...f, photo_url: data.url }));
      toast.success("Photo uploaded");
    } catch {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.quote.trim() || !form.client_name.trim()) {
      toast.error("Client name and quote are required");
      return;
    }
    setSaving(true);
    try {
      if (isEdit) {
        await api.put(`/admin/testimonials/${id}`, form);
        toast.success("Testimonial updated.");
      } else {
        await api.post("/admin/testimonials", form);
        toast.success("Testimonial added.");
      }
      navigate("/admin");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <main data-testid="admin-testimonial-form" className="min-h-screen bg-white pb-32">
      <header className="border-b border-black/10 px-6 md:px-12 py-6 sticky top-0 bg-white z-10">
        <Link
          to="/admin"
          className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.24em] hover:opacity-60"
        >
          <ArrowLeft size={14} /> Dashboard
        </Link>
      </header>

      <div className="max-w-[1000px] mx-auto px-6 md:px-12 pt-16">
        <p className="overline mb-4">{isEdit ? "Edit testimonial" : "New testimonial"}</p>
        <h1 className="font-heading text-4xl md:text-5xl tracking-tight mb-16">
          {isEdit ? (
            <>Update <em className="italic font-light">testimonial</em></>
          ) : (
            <>Add a new <em className="italic font-light">testimonial</em></>
          )}
        </h1>

        <form onSubmit={handleSave} className="space-y-16">
          <section>
            <p className="overline mb-8">Client photo</p>
            <div className="flex items-center gap-8">
              {form.photo_url ? (
                <div className="relative">
                  <img
                    src={resolveImage(form.photo_url)}
                    alt="Client"
                    className="w-28 h-28 rounded-full object-cover grayscale"
                  />
                  <button
                    type="button"
                    onClick={() => setForm((f) => ({ ...f, photo_url: "" }))}
                    className="absolute -top-2 -right-2 bg-white border border-black/15 rounded-full w-8 h-8 flex items-center justify-center hover:bg-red-50 hover:text-red-600"
                    data-testid="remove-photo"
                  >
                    <X size={14} />
                  </button>
                </div>
              ) : (
                <div className="w-28 h-28 rounded-full bg-gray-100 flex items-center justify-center text-[10px] uppercase tracking-widest text-black/30">
                  No photo
                </div>
              )}
              <div>
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  disabled={uploading}
                  data-testid="upload-photo-button"
                  className="inline-flex items-center gap-2 px-6 py-3 border border-black/15 hover:border-black text-[11px] uppercase tracking-[0.24em] transition-colors disabled:opacity-50"
                >
                  <Upload size={14} /> {uploading ? "Uploading..." : form.photo_url ? "Replace" : "Upload photo"}
                </button>
                <p className="text-xs text-black/40 mt-3 max-w-xs">
                  Square images work best — round crop is automatic. Rendered in B&W to match the brand.
                </p>
              </div>
            </div>
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              onChange={handleUpload}
              className="hidden"
            />
          </section>

          <section>
            <p className="overline mb-8">Client</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-8">
              <Field
                label="Client Name"
                name="client_name"
                value={form.client_name}
                onChange={handleChange}
                placeholder="e.g. The Hernandez Family"
                testid="t-client-name"
              />
              <Field
                label="City"
                name="city"
                value={form.city}
                onChange={handleChange}
                placeholder="e.g. Beverly Hills, CA"
                testid="t-city"
              />
              <label className="block">
                <span className="block text-[10px] tracking-[0.28em] uppercase text-black/50 mb-3">
                  Transaction Type
                </span>
                <select
                  data-testid="t-type"
                  name="transaction_type"
                  value={form.transaction_type}
                  onChange={handleChange}
                  className="w-full bg-transparent border-0 border-b border-black/20 rounded-none px-0 py-3 text-base focus:outline-none focus:border-black"
                >
                  {["Buyer", "Seller", "Buyer & Seller", "Investor", "Other"].map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </label>
              <Field
                label="Year"
                name="year"
                value={form.year}
                onChange={handleChange}
                placeholder="2025"
                testid="t-year"
              />
              <Field
                label="Context (the why)"
                name="context"
                value={form.context}
                onChange={handleChange}
                placeholder="e.g. Sold in 11 days · First home · Relocated from NY"
                testid="t-context"
                full
              />
              <Field
                label="Quote"
                name="quote"
                value={form.quote}
                onChange={handleChange}
                multiline
                full
                placeholder="A great testimonial focuses on what made working with Manzano different — the negotiation, the discretion, the timing."
                testid="t-quote"
              />
            </div>
          </section>

          <section>
            <p className="overline mb-8">Visibility</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-8">
              <Field
                label="Sort Position (lower = first)"
                name="position"
                type="number"
                value={form.position}
                onChange={handleChange}
                testid="t-position"
              />
              <label className="flex items-center gap-4 cursor-pointer pt-8">
                <input
                  type="checkbox"
                  name="featured"
                  checked={form.featured}
                  onChange={handleChange}
                  data-testid="t-featured"
                  className="w-5 h-5 accent-black"
                />
                <span className="text-sm">Show on homepage</span>
              </label>
            </div>
          </section>

          <div className="flex flex-col sm:flex-row gap-4 pt-8 border-t border-black/10">
            <button
              type="submit"
              disabled={saving}
              data-testid="t-save"
              className="inline-flex items-center justify-center px-12 py-4 bg-black text-white border border-black text-[11px] uppercase tracking-[0.28em] hover:bg-white hover:text-black transition-all duration-500 disabled:opacity-50"
            >
              {saving ? "Saving..." : isEdit ? "Save changes" : "Publish testimonial"}
            </button>
            <Link
              to="/admin"
              className="inline-flex items-center justify-center px-10 py-4 bg-transparent text-black border border-black text-[11px] uppercase tracking-[0.28em] hover:bg-black hover:text-white transition-all duration-500"
            >
              Cancel
            </Link>
          </div>
        </form>
      </div>
    </main>
  );
};

export default TestimonialForm;
